import {Entity} from "./entity";
import {User} from "./user.entity";
import {Public} from "../../decorators/public.decorator";
import {Expose, Transform} from "class-transformer";
import {container} from "tsyringe";
import {EntityManager} from "../../persistence/entity.manager";
import {UserRepository} from "../repositories/user.repository";
import {DateTime} from "luxon";

export class AuditLog<TEntity extends Entity<TEntity>> extends Entity<AuditLog<TEntity>> {

    /**
     * Re-declared as an accessor pair (not a field) because Entity defines `uid` as an
     * accessor. It keeps AuditLog's own @Public/@Expose at this position in the member list
     * and delegates storage to Entity, which is where the value went before as well.
     */
    @Public()
    @Expose()
    public override get uid(): string {
        return super.uid;
    }

    public override set uid(value: string) {
        super.uid = value;
    }

    @Public()
    @Expose()
    public transactionUid!: string;

    @Public()
    @Expose()
    @Transform(({obj}) => {
        if (!(obj.entityUid && obj.entityClass)) {
            return null;
        }

        const repository = container.resolve(EntityManager).getRepositoryForEntityByName(obj.entityClass);
        if (!repository) {
            return null;
        }

        const promise = repository.findOneById(obj.entityUid);
        Reflect.set(promise, `uid`, obj.entityUid);

        return promise;
    }, {toClassOnly: true})
    public entity!: Promise<TEntity> | null;

    @Public()
    @Expose()
    public entityClass!: string;

    @Public()
    @Expose()
    public entityUid!: string;

    @Public()
    @Expose()
    public commandUid!: string;

    @Public()
    @Expose()
    public commandName!: string;

    @Public()
    @Expose()
    public action!: string;

    @Public()
    @Expose()
    public property!: string;

    @Public()
    @Expose()
    public oldValue: any;

    @Public()
    @Expose()
    public newValue: any;

    @Public()
    @Expose()
    @Transform(({obj}) => {
        if (!obj.userUid) {
            return null;
        }

        return container.resolve(UserRepository).findOneById(obj.userUid)
    })
    public user!: Promise<User> | null;

    @Public()
    @Expose()
    @Transform(({value}) => value ? DateTime.fromSQL(value, {zone: `utc`}) : null, {toClassOnly: true})
    public override get createdAt(): DateTime {
        return super.createdAt;
    }

    /**
     * Accessor pair (not a field) for the same reason as `uid`: Entity owns the storage, this
     * class only adds the @Transform that turns the server's SQL timestamp into a DateTime.
     */
    public override set createdAt(value: DateTime) {
        super.createdAt = value;
    }

    /**
     * Identifies the logged change by the entity it belongs to: the server's entity class
     * name followed by that entity's uid.
     */
    public toString(): string {
        return `${this.entityClass} ${this.entityUid}`;
    }
}